import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Calendar } from '../ui/calendar';
import SignatureCanvas from 'react-signature-canvas';
import { 
  Loader2, CalendarDays, Clock, Users, MapPin, 
  User, Phone, Mail, FileText, ChevronRight, ChevronLeft,
  Check, AlertCircle, CheckCircle2, XCircle, PenTool, RotateCcw,
  UserPlus, UserCheck, ShieldCheck
} from 'lucide-react';
import { bookingAPI, toursAPI, authAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { format, parseISO, isSameDay } from 'date-fns';

// Convert 24h time to 12h AM/PM format
const formatTimeToAMPM = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

export function BookingModal({ tour, isOpen, onClose, preSelectedDate, preSelectedTime, preSelectedGuests }) {
  // If pre-selected values exist, skip step 1 (date/time selection)
  const initialStep = (preSelectedDate && preSelectedTime) ? 2 : 1;
  const [step, setStep] = useState(initialStep);
  const [loading, setLoading] = useState(false);
  const [hotels, setHotels] = useState([]);
  const [waiver, setWaiver] = useState(null);
  const [showWaiver, setShowWaiver] = useState(false);
  const [waiverGuestIndex, setWaiverGuestIndex] = useState(null);
  const [availabilitySlots, setAvailabilitySlots] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [timeSlotsForDate, setTimeSlotsForDate] = useState([]);
  
  // Auth context
  const { user, isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  
  // Checkout type state (guest vs account)
  const [checkoutType, setCheckoutType] = useState(null); // 'guest' | 'account' | 'login'
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [accountForm, setAccountForm] = useState({ password: '', confirmPassword: '' });
  const [creatingAccount, setCreatingAccount] = useState(false);
  
  // Form state - use pre-selected values if available
  const [selectedDate, setSelectedDate] = useState(preSelectedDate || null);
  const [selectedTime, setSelectedTime] = useState(preSelectedTime || '');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [guests, setGuests] = useState(preSelectedGuests || 1);
  const [bookerInfo, setBookerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    secondaryPhone: '',
    isCruiseTraveler: false,
    cruiseLine: '',
  });
  const [guestInfo, setGuestInfo] = useState([]);
  const [pickupHotel, setPickupHotel] = useState('');
  const [specialRequests, setSpecialRequests] = useState('');
  const [customHotelAddress, setCustomHotelAddress] = useState('');
  
  // Reset state when modal opens with new pre-selected values
  useEffect(() => {
    if (isOpen) {
      if (preSelectedDate) setSelectedDate(preSelectedDate);
      if (preSelectedTime) setSelectedTime(preSelectedTime);
      if (preSelectedGuests) setGuests(preSelectedGuests);
      // Set initial step based on pre-selection
      setStep((preSelectedDate && preSelectedTime) ? 2 : 1);
    }
  }, [isOpen, preSelectedDate, preSelectedTime, preSelectedGuests]);

  // Load hotels, waiver, and availability
  useEffect(() => {
    if (isOpen && tour) {
      loadData();
    }
  }, [isOpen, tour]);

  // Update time slots when date changes
  useEffect(() => {
    if (selectedDate && availabilitySlots.length > 0) {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const slotsForDate = availabilitySlots.filter(
        slot => slot.date === dateStr && slot.is_available !== false && (slot.available_spots || slot.max_guests - (slot.booked_guests || 0)) > 0
      );
      setTimeSlotsForDate(slotsForDate);
      // Only reset time if not pre-selected
      if (!preSelectedTime) {
        setSelectedTime('');
        setSelectedSlot(null);
      }
    }
  }, [selectedDate, availabilitySlots, preSelectedTime]);

  // Initialize guest info when guests change
  useEffect(() => {
    const newGuestInfo = [];
    for (let i = 0; i < guests; i++) {
      newGuestInfo.push(guestInfo[i] || {
        full_name: i === 0 ? bookerInfo.name : '',
        date_of_birth: '',
        waiver_signed: false,
        signature_data: null, // Store signature image data
      });
    }
    setGuestInfo(newGuestInfo);
  }, [guests]);

  // Signature canvas ref
  const signatureRef = useRef(null);

  const loadData = async () => {
    try {
      const [hotelsRes, waiverRes, availRes] = await Promise.all([
        bookingAPI.getPickupHotels(),
        bookingAPI.getWaiver(),
        toursAPI.getAvailabilitySlots(tour.id),
      ]);
      setHotels(hotelsRes.data || []);
      setWaiver(waiverRes.data);
      
      const slots = availRes.data?.slots || [];
      setAvailabilitySlots(slots);
      
      // Extract unique available dates
      const uniqueDates = [...new Set(slots.map(s => s.date))];
      setAvailableDates(uniqueDates.map(d => parseISO(d)));
    } catch (error) {
      console.error('Error loading booking data:', error);
    }
  };

  const updateGuestInfo = (index, field, value) => {
    const updated = [...guestInfo];
    updated[index] = { ...updated[index], [field]: value };
    setGuestInfo(updated);
  };

  const openWaiverForGuest = (index) => {
    setWaiverGuestIndex(index);
    setShowWaiver(true);
  };

  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  };

  const signWaiver = () => {
    if (waiverGuestIndex !== null && signatureRef.current) {
      // Check if signature is empty
      if (signatureRef.current.isEmpty()) {
        toast.error('Please sign the waiver using your finger or mouse');
        return;
      }
      
      // Get signature as base64 image
      const signatureData = signatureRef.current.toDataURL('image/png');
      
      // Update guest info with signature
      const updated = [...guestInfo];
      updated[waiverGuestIndex] = { 
        ...updated[waiverGuestIndex], 
        waiver_signed: true,
        signature_data: signatureData
      };
      setGuestInfo(updated);
      
      setShowWaiver(false);
      setWaiverGuestIndex(null);
      toast.success('Waiver signed successfully!');
    }
  };

  const calculatePrice = () => {
    let price = tour.price * guests;
    
    // Apply group discount
    if (tour.enable_dynamic_pricing && guests >= (tour.group_discount_threshold || 5)) {
      const discount = tour.group_discount_percent || 10;
      price = price * (1 - discount / 100);
    }
    
    return price.toFixed(2);
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        if (!selectedDate || !selectedTime) {
          toast.error('Please select a date and time');
          return false;
        }
        return true;
      case 2:
        if (!bookerInfo.name || !bookerInfo.email || !bookerInfo.phone) {
          toast.error('Please fill in all required fields');
          return false;
        }
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(bookerInfo.email)) {
          toast.error('Please enter a valid email address');
          return false;
        }
        // Validate phone number
        const phoneRegex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;
        if (!phoneRegex.test(bookerInfo.phone.replace(/\s/g, ''))) {
          toast.error('Please enter a valid phone number');
          return false;
        }
        return true;
      case 3:
        // Only require main booker (guest 0) to sign waiver during checkout
        // Other guests will receive waiver links via email after booking
        for (let i = 0; i < guests; i++) {
          if (!guestInfo[i]?.full_name || !guestInfo[i]?.date_of_birth) {
            toast.error(`Please fill in all information for Guest ${i + 1}`);
            return false;
          }
        }
        // Only check waiver for main booker (index 0)
        if (!guestInfo[0]?.waiver_signed) {
          toast.error(`Please sign the waiver to continue`);
          return false;
        }
        return true;
      case 4:
        // Skip validation if pickup step should be skipped (cruise travelers or no pickup required)
        if (!tour.show_pickup_location || bookerInfo.isCruiseTraveler) {
          return true;
        }
        if (!pickupHotel) {
          toast.error('Please select a pickup location');
          return false;
        }
        // Validate custom hotel address if "Hotel Not Listed" is selected
        if (pickupHotel === 'HOTEL_NOT_LISTED' && !customHotelAddress.trim()) {
          toast.error('Please enter your hotel/accommodation address');
          return false;
        }
        return true;
      case 5:
        // Checkout type validation
        if (!isAuthenticated && !checkoutType) {
          toast.error('Please choose how to continue');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep()) {
      // Skip pickup step if not needed OR if user is a cruise traveler
      if (step === 3 && (!tour.show_pickup_location || bookerInfo.isCruiseTraveler)) {
        setStep(5); // Go to checkout type step
      } else if (step === 5) {
        // Checkout type step - only proceed if type is selected
        if (!checkoutType && !isAuthenticated) {
          toast.error('Please choose how to continue');
          return;
        }
        setStep(6); // Go to review step
      } else {
        setStep(step + 1);
      }
    }
  };

  const prevStep = () => {
    // Skip pickup step if not needed OR if user is a cruise traveler
    if (step === 6 && (!tour.show_pickup_location || bookerInfo.isCruiseTraveler)) {
      setStep(5); // Go back to checkout type step
    } else if (step === 5 && (!tour.show_pickup_location || bookerInfo.isCruiseTraveler)) {
      setStep(3);
    } else {
      setStep(step - 1);
    }
  };
  
  // Handle account creation during checkout
  const handleCreateAccount = async () => {
    if (accountForm.password !== accountForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (accountForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setCreatingAccount(true);
    try {
      // Register new account
      await authAPI.register({
        name: bookerInfo.name,
        email: bookerInfo.email,
        password: accountForm.password,
        phone: bookerInfo.phone
      });
      
      // Login with new account
      await login(bookerInfo.email, accountForm.password);
      
      toast.success('Account created! Continuing to payment...');
      setCheckoutType('account');
      setShowAccountForm(false);
      setStep(6); // Proceed to review
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setCreatingAccount(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Determine pickup location display value
      let pickupLocationDisplay = pickupHotel;
      if (pickupHotel === 'HOTEL_NOT_LISTED') {
        pickupLocationDisplay = `Custom Address: ${customHotelAddress}`;
      } else if (pickupHotel === 'ASK_ME_LATER') {
        pickupLocationDisplay = 'To Be Confirmed';
      }

      // Combine special requests with custom hotel address if needed
      let finalSpecialRequests = specialRequests;
      if (pickupHotel === 'HOTEL_NOT_LISTED' && customHotelAddress) {
        finalSpecialRequests = `Custom Hotel/Address: ${customHotelAddress}\n${specialRequests}`.trim();
      }

      // Create booking (no login required - guest checkout)
      const bookingData = {
        tour_id: tour.id,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time: selectedTime,
        guests,
        booker_name: bookerInfo.name,
        booker_email: bookerInfo.email,
        booker_phone: bookerInfo.phone,
        booker_secondary_phone: bookerInfo.secondaryPhone || null,
        is_cruise_traveler: bookerInfo.isCruiseTraveler,
        cruise_line: bookerInfo.cruiseLine || null,
        guest_info: guestInfo,
        pickup_hotel: pickupLocationDisplay || null,
        pickup_location: pickupHotel ? 'hotel' : null,
        special_requests: finalSpecialRequests,
        referral_code: sessionStorage.getItem('referral_code') || null,
      };

      const bookingRes = await bookingAPI.create(bookingData);
      const booking = bookingRes.data;

      // Create checkout session
      const checkoutRes = await bookingAPI.createCheckout(booking.id);
      
      // Redirect to Stripe checkout
      if (checkoutRes.data?.checkout_url) {
        window.location.href = checkoutRes.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  // Check if a date is available (has slots with capacity)
  const isDateAvailable = (date) => {
    return availableDates.some(d => isSameDay(d, date));
  };

  // Get max guests for selected slot
  const getMaxGuestsForSlot = () => {
    if (selectedSlot) {
      return Math.min(
        selectedSlot.max_guests - (selectedSlot.booked_guests || 0),
        selectedSlot.max_guests
      );
    }
    return tour.max_guests;
  };

  const handleTimeSelect = (slot) => {
    setSelectedTime(slot.time);
    setSelectedSlot(slot);
    // Reset guests if more than available
    const maxAvailable = slot.max_guests - (slot.booked_guests || 0);
    if (guests > maxAvailable) {
      setGuests(Math.max(1, maxAvailable));
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-primary" />
              Book: {tour.title}
            </DialogTitle>
          </DialogHeader>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-6">
            {[1, 2, 3, 4, 5, 6].map((s) => (
              <div 
                key={s} 
                className={`flex items-center ${s < 6 ? 'flex-1' : ''}`}
              >
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    s === step ? 'bg-primary text-white' : 
                    s < step ? 'bg-green-500 text-white' : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {s < step ? <Check className="h-4 w-4" /> : s}
                </div>
                {s < 6 && (
                  <div className={`flex-1 h-1 mx-2 ${s < step ? 'bg-green-500' : 'bg-muted'}`} />
                )}
              </div>
            ))}
          </div>

          {/* Step 1: Date & Time */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <Label className="text-lg font-semibold mb-4 block">Select Date</Label>
                {availableDates.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CalendarDays className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No available dates for this tour.</p>
                    <p className="text-sm">Please check back later or contact us.</p>
                  </div>
                ) : (
                  <div className="flex justify-center">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => {
                        setSelectedDate(date);
                        setSelectedTime('');
                        setSelectedSlot(null);
                      }}
                      disabled={(date) => date < new Date() || !isDateAvailable(date)}
                      modifiers={{
                        available: availableDates
                      }}
                      modifiersStyles={{
                        available: { backgroundColor: 'rgba(34, 197, 94, 0.1)' }
                      }}
                      className="rounded-md border"
                    />
                  </div>
                )}
              </div>

              {selectedDate && (
                <div>
                  <Label className="text-lg font-semibold mb-4 block">Select Time</Label>
                  {timeSlotsForDate.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">
                      No available time slots for this date.
                    </p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {timeSlotsForDate.map((slot) => {
                        const availableSpots = slot.max_guests - (slot.booked_guests || 0);
                        return (
                          <button
                            key={slot.id || `${slot.date}-${slot.time}`}
                            onClick={() => handleTimeSelect(slot)}
                            className={`p-3 rounded-lg border text-center transition-colors ${
                              selectedTime === slot.time 
                                ? 'bg-primary text-white border-primary' 
                                : 'hover:border-primary'
                            }`}
                          >
                            <Clock className="h-4 w-4 mx-auto mb-1" />
                            <div className="font-medium">{formatTimeToAMPM(slot.time)}</div>
                            <div className={`text-xs mt-1 ${selectedTime === slot.time ? 'text-white/80' : 'text-muted-foreground'}`}>
                              {availableSpots} {availableSpots === 1 ? 'spot' : 'spots'} left
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {selectedTime && (
                <div>
                  <Label className="text-lg font-semibold mb-4 block">Number of Guests</Label>
                  <div className="flex items-center gap-4">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setGuests(Math.max(1, guests - 1))}
                      disabled={guests <= 1}
                    >
                      -
                    </Button>
                    <span className="text-2xl font-bold w-12 text-center">{guests}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setGuests(Math.min(getMaxGuestsForSlot(), guests + 1))}
                      disabled={guests >= getMaxGuestsForSlot()}
                    >
                      +
                    </Button>
                    <span className="text-muted-foreground">
                      Max: {getMaxGuestsForSlot()} available
                    </span>
                  </div>
                </div>
              )}

              {/* Price Summary */}
              {selectedTime && (
                <Card className="p-4 bg-primary/5">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Total Price:</span>
                    <span className="text-2xl font-bold text-primary">${calculatePrice()}</span>
                  </div>
                  {tour.enable_dynamic_pricing && guests >= (tour.group_discount_threshold || 5) && (
                    <p className="text-sm text-green-600 mt-1">
                      ✓ Group discount applied ({tour.group_discount_percent || 10}% off)
                    </p>
                  )}
                </Card>
              )}
            </div>
          )}

          {/* Step 2: Booker Information */}
          {step === 2 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                Primary Contact Information
              </h3>
              <p className="text-sm text-muted-foreground">
                This information will be used for booking confirmation and tour updates.
              </p>
              
              <div className="space-y-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    value={bookerInfo.name}
                    onChange={(e) => setBookerInfo({ ...bookerInfo, name: e.target.value })}
                    placeholder="John Doe"
                    data-testid="booker-name"
                  />
                </div>
                <div>
                  <Label>Email Address *</Label>
                  <Input
                    type="email"
                    value={bookerInfo.email}
                    onChange={(e) => setBookerInfo({ ...bookerInfo, email: e.target.value })}
                    placeholder="john@example.com"
                    data-testid="booker-email"
                  />
                </div>
                <div>
                  <Label>Phone Number * (Important for tour coordination)</Label>
                  <Input
                    type="tel"
                    value={bookerInfo.phone}
                    onChange={(e) => setBookerInfo({ ...bookerInfo, phone: e.target.value })}
                    placeholder="+1 (787) 555-0123"
                    data-testid="booker-phone"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    We'll use this to contact you about your tour
                  </p>
                </div>
                <div>
                  <Label>Secondary Phone Number (Optional)</Label>
                  <Input
                    type="tel"
                    value={bookerInfo.secondaryPhone}
                    onChange={(e) => setBookerInfo({ ...bookerInfo, secondaryPhone: e.target.value })}
                    placeholder="+1 (787) 555-0124"
                    data-testid="booker-secondary-phone"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Backup contact number (optional)
                  </p>
                </div>

                {/* Cruise Traveler Question */}
                <Card className="p-4 bg-blue-50/50 border-blue-200">
                  <div className="flex items-start gap-3">
                    <button
                      type="button"
                      onClick={() => setBookerInfo({ ...bookerInfo, isCruiseTraveler: !bookerInfo.isCruiseTraveler, cruiseLine: !bookerInfo.isCruiseTraveler ? bookerInfo.cruiseLine : '' })}
                      className={`mt-0.5 w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                        bookerInfo.isCruiseTraveler 
                          ? 'bg-primary border-primary text-white' 
                          : 'border-gray-300 hover:border-primary'
                      }`}
                      data-testid="cruise-traveler-checkbox"
                    >
                      {bookerInfo.isCruiseTraveler && <Check className="h-4 w-4" />}
                    </button>
                    <div className="flex-1">
                      <Label className="text-base font-semibold cursor-pointer" onClick={() => setBookerInfo({ ...bookerInfo, isCruiseTraveler: !bookerInfo.isCruiseTraveler, cruiseLine: !bookerInfo.isCruiseTraveler ? bookerInfo.cruiseLine : '' })}>
                        Are you a Cruise Traveler?
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        {bookerInfo.isCruiseTraveler 
                          ? "Great! We'll pick you up directly at your cruise port. No hotel pickup needed."
                          : "Check this box if you're arriving via cruise ship. This helps us coordinate pickup times with your ship schedule."
                        }
                      </p>
                      
                      {/* Cruise Line Selector - shows when cruise traveler is checked */}
                      {bookerInfo.isCruiseTraveler && (
                        <div className="mt-4 pt-4 border-t border-blue-200">
                          <Label className="text-sm font-medium mb-3 block">Select your cruise line:</Label>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {[
                              { id: 'carnival', name: 'Carnival', color: 'bg-red-500' },
                              { id: 'disney', name: 'Disney', color: 'bg-blue-600' },
                              { id: 'royal', name: 'Royal Caribbean', color: 'bg-blue-800' },
                              { id: 'norwegian', name: 'Norwegian', color: 'bg-blue-400' },
                              { id: 'celebrity', name: 'Celebrity', color: 'bg-gray-800' },
                              { id: 'msc', name: 'MSC', color: 'bg-yellow-500' },
                              { id: 'virgin', name: 'Virgin', color: 'bg-red-600' },
                              { id: 'holland', name: 'Holland America', color: 'bg-orange-600' },
                            ].map((cruise) => (
                              <button
                                key={cruise.id}
                                type="button"
                                onClick={() => setBookerInfo({ ...bookerInfo, cruiseLine: cruise.id })}
                                className={`flex items-center gap-2 p-2 rounded-lg border-2 transition-all ${
                                  bookerInfo.cruiseLine === cruise.id
                                    ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                                    : 'border-gray-200 hover:border-gray-300 bg-white'
                                }`}
                                data-testid={`cruise-line-${cruise.id}`}
                              >
                                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                  bookerInfo.cruiseLine === cruise.id 
                                    ? 'border-primary' 
                                    : 'border-gray-400'
                                }`}>
                                  {bookerInfo.cruiseLine === cruise.id && (
                                    <div className="w-2 h-2 rounded-full bg-primary" />
                                  )}
                                </div>
                                <span className={`w-3 h-3 rounded-full ${cruise.color}`} />
                                <span className="text-xs font-medium truncate">{cruise.name}</span>
                              </button>
                            ))}
                          </div>
                          
                          {/* Not Listed / Ask Later option */}
                          <button
                            type="button"
                            onClick={() => setBookerInfo({ ...bookerInfo, cruiseLine: 'not_listed' })}
                            className={`mt-2 w-full flex items-center gap-2 p-2 rounded-lg border-2 transition-all ${
                              bookerInfo.cruiseLine === 'not_listed'
                                ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                                : 'border-gray-200 hover:border-gray-300 bg-white'
                            }`}
                            data-testid="cruise-line-not-listed"
                          >
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                              bookerInfo.cruiseLine === 'not_listed' 
                                ? 'border-primary' 
                                : 'border-gray-400'
                            }`}>
                              {bookerInfo.cruiseLine === 'not_listed' && (
                                <div className="w-2 h-2 rounded-full bg-primary" />
                              )}
                            </div>
                            <span className="text-sm font-medium">Not Listed / Ask Me Later</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          )}

          {/* Step 3: Guest Information */}
          {step === 3 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Guest Information
              </h3>
              <p className="text-sm text-muted-foreground">
                Please provide information for all {guests} guest(s).
              </p>

              {guestInfo.map((guest, index) => (
                <Card key={index} className={`p-4 border-2 ${
                  index === 0 
                    ? (guest.waiver_signed ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/30')
                    : 'border-gray-200 bg-gray-50/30'
                }`}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Guest {index + 1}
                      {index === 0 && <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">Primary (You)</span>}
                    </h4>
                    {/* Only show waiver status for main booker during checkout */}
                    {index === 0 && (
                      guest.waiver_signed ? (
                        <div className="flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full">
                          <CheckCircle2 className="h-5 w-5 fill-green-500 text-white" />
                          <span className="text-sm font-medium">Waiver Signed</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 bg-red-100 text-red-700 px-3 py-1 rounded-full animate-pulse">
                          <XCircle className="h-5 w-5" />
                          <span className="text-sm font-medium">Signature Required</span>
                        </div>
                      )
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <Label>Full Name *</Label>
                      <Input
                        value={guest.full_name || ''}
                        onChange={(e) => updateGuestInfo(index, 'full_name', e.target.value)}
                        placeholder="Full name as shown on ID"
                        data-testid={`guest-name-${index}`}
                      />
                      {/* Waiver notice for additional guests - appears under Full Name */}
                      {index > 0 && (
                        <p className="text-xs text-blue-600 mt-1.5 flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          You will receive a waiver link for {guest.full_name || `Guest ${index + 1}`} via email after booking
                        </p>
                      )}
                    </div>
                    <div>
                      <Label>Date of Birth *</Label>
                      <Input
                        type="date"
                        value={guest.date_of_birth || ''}
                        onChange={(e) => updateGuestInfo(index, 'date_of_birth', e.target.value)}
                        data-testid={`guest-dob-${index}`}
                      />
                    </div>
                  </div>

                  {/* Only show waiver signing for main booker (index 0) */}
                  {index === 0 && (
                    <div className="flex items-center gap-4">
                      <Button
                        type="button"
                        variant={guest.waiver_signed ? "outline" : "default"}
                        size="sm"
                        onClick={() => openWaiverForGuest(index)}
                        className={`gap-2 ${!guest.waiver_signed ? 'bg-red-600 hover:bg-red-700' : ''}`}
                        data-testid={`sign-waiver-${index}`}
                      >
                        <PenTool className="h-4 w-4" />
                        {guest.waiver_signed ? 'View Signed Waiver' : 'Sign Waiver Now'}
                      </Button>
                      
                      {guest.waiver_signed && guest.signature_data && (
                        <div className="flex items-center gap-2">
                          <img 
                            src={guest.signature_data} 
                            alt="Signature" 
                            className="h-8 border rounded bg-white"
                          />
                          <span className="text-xs text-green-600">Signature captured</span>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              ))}

              {/* Waiver Status Summary - Only for main booker now */}
              <Card className={`p-4 ${guestInfo[0]?.waiver_signed ? 'bg-green-50 border-green-300' : 'bg-amber-50 border-amber-300'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    <span className="font-medium">Your Waiver Status</span>
                  </div>
                  <div className="text-sm">
                    {guestInfo[0]?.waiver_signed ? (
                      <span className="text-green-600 font-medium flex items-center gap-1">
                        <CheckCircle2 className="h-4 w-4" /> Signed
                      </span>
                    ) : (
                      <span className="text-amber-600 font-medium">Pending signature</span>
                    )}
                  </div>
                </div>
                {!guestInfo[0]?.waiver_signed && (
                  <p className="text-sm text-amber-700 mt-2">
                    Please sign the waiver above to proceed to checkout.
                  </p>
                )}
                {guestInfo[0]?.waiver_signed && guests > 1 && (
                  <p className="text-sm text-green-700 mt-2">
                    Great! After booking, your other guests will receive individual waiver links via email.
                  </p>
                )}
              </Card>
            </div>
          )}

          {/* Step 4: Pickup Location - Skip for cruise travelers */}
          {step === 4 && tour.show_pickup_location && !bookerInfo.isCruiseTraveler && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                Pickup Location
              </h3>
              <p className="text-sm text-muted-foreground">
                Select your pickup location. Our driver will meet you at the lobby.
              </p>

              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                {/* Hotel List */}
                {hotels.map((hotel) => (
                  <button
                    key={hotel.id}
                    onClick={() => setPickupHotel(hotel.name)}
                    className={`w-full p-4 rounded-lg border text-left transition-colors ${
                      pickupHotel === hotel.name 
                        ? 'bg-primary/10 border-primary' 
                        : 'hover:bg-muted'
                    }`}
                    data-testid={`pickup-hotel-${hotel.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        pickupHotel === hotel.name ? 'border-primary bg-primary' : 'border-gray-300'
                      }`}>
                        {pickupHotel === hotel.name && <Check className="h-3 w-3 text-white" />}
                      </div>
                      <div>
                        <div className="font-semibold">{hotel.name}</div>
                        {hotel.address && (
                          <div className="text-sm text-muted-foreground">{hotel.address}</div>
                        )}
                        {hotel.area && (
                          <div className="text-xs text-primary mt-1">{hotel.area}</div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}

                {/* Divider */}
                {hotels.length > 0 && (
                  <div className="border-t my-3 pt-3">
                    <p className="text-xs text-muted-foreground mb-2 font-medium">Other Options</p>
                  </div>
                )}

                {/* Hotel Not Listed Option */}
                <button
                  onClick={() => setPickupHotel('HOTEL_NOT_LISTED')}
                  className={`w-full p-4 rounded-lg border text-left transition-colors ${
                    pickupHotel === 'HOTEL_NOT_LISTED' 
                      ? 'bg-amber-50 border-amber-400' 
                      : 'hover:bg-muted border-dashed'
                  }`}
                  data-testid="pickup-hotel-not-listed"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      pickupHotel === 'HOTEL_NOT_LISTED' ? 'border-amber-500 bg-amber-500' : 'border-gray-300'
                    }`}>
                      {pickupHotel === 'HOTEL_NOT_LISTED' && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <div>
                      <div className="font-semibold">Hotel Not Listed</div>
                      <div className="text-sm text-muted-foreground">
                        My hotel/accommodation is not in the list above
                      </div>
                    </div>
                  </div>
                </button>

                {/* Ask Me Later Option */}
                <button
                  onClick={() => setPickupHotel('ASK_ME_LATER')}
                  className={`w-full p-4 rounded-lg border text-left transition-colors ${
                    pickupHotel === 'ASK_ME_LATER' 
                      ? 'bg-blue-50 border-blue-400' 
                      : 'hover:bg-muted border-dashed'
                  }`}
                  data-testid="pickup-ask-later"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      pickupHotel === 'ASK_ME_LATER' ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                    }`}>
                      {pickupHotel === 'ASK_ME_LATER' && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <div>
                      <div className="font-semibold">Ask Me Later</div>
                      <div className="text-sm text-muted-foreground">
                        I'll provide my pickup location after booking
                      </div>
                    </div>
                  </div>
                </button>
              </div>

              {hotels.length === 0 && (
                <p className="text-center text-muted-foreground py-4">
                  No preset pickup locations available.
                </p>
              )}

              {/* Custom Hotel Name Input - shows when "Hotel Not Listed" is selected */}
              {pickupHotel === 'HOTEL_NOT_LISTED' && (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <Label className="text-amber-800">Please enter your hotel/accommodation name *</Label>
                  <Input
                    value={customHotelAddress}
                    onChange={(e) => setCustomHotelAddress(e.target.value)}
                    placeholder="Enter your hotel name and address"
                    className="mt-2 bg-white"
                    data-testid="custom-hotel-input"
                  />
                  <p className="text-xs text-amber-700 mt-2">
                    We'll contact you to confirm pickup arrangements
                  </p>
                </div>
              )}

              <div>
                <Label>Special Requests (Optional)</Label>
                <textarea
                  value={specialRequests}
                  onChange={(e) => setSpecialRequests(e.target.value)}
                  placeholder="Any special requirements or notes..."
                  className="w-full px-3 py-2 border rounded-lg resize-none"
                  rows={3}
                  data-testid="special-requests"
                />
              </div>
            </div>
          )}

          {/* Step 5: Checkout Type (Guest vs Account) */}
          {step === 5 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">How would you like to continue?</h3>
              
              {isAuthenticated ? (
                <Card className="p-4 border-2 border-green-500 bg-green-50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-full">
                      <UserCheck className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-green-900">Logged in as {user?.name || user?.email}</p>
                      <p className="text-sm text-green-700">Your booking will be saved to your account</p>
                    </div>
                  </div>
                </Card>
              ) : (
                <div className="space-y-3">
                  {/* Guest Checkout Option */}
                  <Card 
                    className={`p-4 cursor-pointer transition-all ${checkoutType === 'guest' ? 'border-2 border-primary bg-primary/5' : 'hover:border-primary/50'}`}
                    onClick={() => { setCheckoutType('guest'); setShowAccountForm(false); }}
                    data-testid="guest-checkout-option"
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-full ${checkoutType === 'guest' ? 'bg-primary text-white' : 'bg-muted'}`}>
                        <User className="h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold">Continue as Guest</p>
                        <p className="text-sm text-muted-foreground">Quick checkout without creating an account</p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                          <ShieldCheck className="h-4 w-4" />
                          <span>Your booking confirmation will be sent to your email</span>
                        </div>
                      </div>
                      {checkoutType === 'guest' && (
                        <CheckCircle2 className="h-6 w-6 text-primary" />
                      )}
                    </div>
                  </Card>

                  {/* Create Account Option */}
                  <Card 
                    className={`p-4 cursor-pointer transition-all ${checkoutType === 'account' || showAccountForm ? 'border-2 border-primary bg-primary/5' : 'hover:border-primary/50'}`}
                    onClick={() => { setCheckoutType('account'); setShowAccountForm(true); }}
                    data-testid="create-account-option"
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-full ${checkoutType === 'account' || showAccountForm ? 'bg-primary text-white' : 'bg-muted'}`}>
                        <UserPlus className="h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold">Create an Account</p>
                        <p className="text-sm text-muted-foreground">Save your bookings, manage payments, and more</p>
                        <ul className="text-xs text-muted-foreground mt-2 space-y-1">
                          <li>• View all your bookings in one place</li>
                          <li>• Easy rebooking and cancellations</li>
                          <li>• Manage waivers for your group</li>
                        </ul>
                      </div>
                      {(checkoutType === 'account' || showAccountForm) && (
                        <CheckCircle2 className="h-6 w-6 text-primary" />
                      )}
                    </div>

                    {/* Account Creation Form */}
                    {showAccountForm && (
                      <div className="mt-4 pt-4 border-t space-y-3" onClick={(e) => e.stopPropagation()}>
                        <div>
                          <Label>Create a Password</Label>
                          <Input 
                            type="password"
                            placeholder="At least 6 characters"
                            value={accountForm.password}
                            onChange={(e) => setAccountForm({...accountForm, password: e.target.value})}
                            data-testid="account-password"
                          />
                        </div>
                        <div>
                          <Label>Confirm Password</Label>
                          <Input 
                            type="password"
                            placeholder="Confirm your password"
                            value={accountForm.confirmPassword}
                            onChange={(e) => setAccountForm({...accountForm, confirmPassword: e.target.value})}
                            data-testid="account-confirm-password"
                          />
                        </div>
                        <Button 
                          onClick={handleCreateAccount} 
                          disabled={creatingAccount}
                          className="w-full"
                          data-testid="create-account-btn"
                        >
                          {creatingAccount && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                          Create Account & Continue
                        </Button>
                      </div>
                    )}
                  </Card>

                  {/* Login Option */}
                  <div className="text-center pt-2">
                    <p className="text-sm text-muted-foreground">
                      Already have an account?{' '}
                      <button
                        type="button"
                        onClick={() => {
                          // Store booking state and redirect to login
                          sessionStorage.setItem('pendingBooking', JSON.stringify({
                            tourId: tour.id,
                            date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null,
                            time: selectedTime,
                            guests,
                            bookerInfo,
                            guestInfo,
                            pickupHotel,
                            specialRequests
                          }));
                          navigate('/login?redirect=' + encodeURIComponent(`/tours/${tour.id}?resumeBooking=true`));
                          onClose();
                        }}
                        className="text-primary font-semibold hover:underline"
                        data-testid="login-link"
                      >
                        Log in
                      </button>
                    </p>
                  </div>
                </div>
              )}

              {/* Validation Notice for Guest Checkout */}
              {checkoutType === 'guest' && (
                <Card className="p-3 bg-blue-50 border-blue-200">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium">Guest checkout requires:</p>
                      <ul className="mt-1 space-y-1">
                        <li>• Valid email address (for booking confirmation)</li>
                        <li>• Valid phone number (for tour updates)</li>
                      </ul>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* Step 6: Review & Pay */}
          {step === 6 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Review Your Booking</h3>
              
              <Card className="p-4">
                <h4 className="font-semibold mb-2">Tour Details</h4>
                <div className="space-y-1 text-sm">
                  <p><strong>Tour:</strong> {tour.title}</p>
                  <p><strong>Date:</strong> {selectedDate && format(selectedDate, 'MMMM d, yyyy')}</p>
                  <p><strong>Time:</strong> {formatTimeToAMPM(selectedTime)}</p>
                  <p><strong>Guests:</strong> {guests}</p>
                </div>
              </Card>

              <Card className="p-4">
                <h4 className="font-semibold mb-2">Contact Information</h4>
                <div className="space-y-1 text-sm">
                  <p><strong>Name:</strong> {bookerInfo.name}</p>
                  <p><strong>Email:</strong> {bookerInfo.email}</p>
                  <p><strong>Phone:</strong> {bookerInfo.phone}</p>
                  {bookerInfo.secondaryPhone && (
                    <p><strong>Secondary Phone:</strong> {bookerInfo.secondaryPhone}</p>
                  )}
                  {bookerInfo.isCruiseTraveler && (
                    <div className="mt-2 p-2 bg-blue-50 rounded-lg">
                      <p className="flex items-center gap-2 text-blue-600">
                        <Check className="h-4 w-4" />
                        <strong>Cruise Traveler</strong>
                      </p>
                      {bookerInfo.cruiseLine && (
                        <p className="text-sm text-blue-700 ml-6 mt-1">
                          Cruise Line: <strong>
                            {bookerInfo.cruiseLine === 'not_listed' ? 'Not Listed / Ask Later' : 
                             bookerInfo.cruiseLine === 'carnival' ? 'Carnival' :
                             bookerInfo.cruiseLine === 'disney' ? 'Disney' :
                             bookerInfo.cruiseLine === 'royal' ? 'Royal Caribbean' :
                             bookerInfo.cruiseLine === 'norwegian' ? 'Norwegian' :
                             bookerInfo.cruiseLine === 'celebrity' ? 'Celebrity' :
                             bookerInfo.cruiseLine === 'msc' ? 'MSC' :
                             bookerInfo.cruiseLine === 'virgin' ? 'Virgin' :
                             bookerInfo.cruiseLine === 'holland' ? 'Holland America' :
                             bookerInfo.cruiseLine}
                          </strong>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </Card>

              <Card className="p-4">
                <h4 className="font-semibold mb-2">Guests</h4>
                <div className="space-y-2 text-sm">
                  {guestInfo.map((guest, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span>{index + 1}. {guest.full_name}</span>
                      {index === 0 ? (
                        <span className="text-green-600 flex items-center gap-1">
                          <CheckCircle2 className="h-4 w-4" /> Waiver signed
                        </span>
                      ) : (
                        <span className="text-blue-600 flex items-center gap-1 text-xs">
                          <Mail className="h-3 w-3" /> Waiver link will be sent via email
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </Card>

              {pickupHotel && (
                <Card className="p-4">
                  <h4 className="font-semibold mb-2">Pickup Location</h4>
                  <p className="text-sm">
                    {pickupHotel === 'HOTEL_NOT_LISTED' ? (
                      <span className="text-amber-600">
                        <strong>Custom Address:</strong> {customHotelAddress}
                      </span>
                    ) : pickupHotel === 'ASK_ME_LATER' ? (
                      <span className="text-blue-600">To Be Confirmed After Booking</span>
                    ) : (
                      pickupHotel
                    )}
                  </p>
                </Card>
              )}

              <Card className="p-4 bg-primary/5">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">Total to Pay:</span>
                  <span className="text-2xl font-bold text-primary">${calculatePrice()}</span>
                </div>
              </Card>

            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-6">
            {step > 1 ? (
              <Button variant="outline" onClick={prevStep}>
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            ) : (
              <div />
            )}

            {step < 6 ? (
              <Button onClick={nextStep} disabled={step === 5 && showAccountForm}>
                {step === 5 ? 'Continue to Review' : 'Next'}
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit} 
                disabled={loading}
                className="gap-2 bg-green-600 hover:bg-green-700"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Proceed to Payment
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Waiver Modal with Signature Pad */}
      <Dialog open={showWaiver} onOpenChange={setShowWaiver}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              {waiver?.title || 'Liability Waiver'}
            </DialogTitle>
          </DialogHeader>
          
          {waiverGuestIndex !== null && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-blue-800 text-sm font-medium">
                Signing for: {guestInfo[waiverGuestIndex]?.full_name || `Guest ${waiverGuestIndex + 1}`}
              </p>
            </div>
          )}
          
          <div 
            className="prose prose-sm max-w-none bg-gray-50 p-4 rounded-lg max-h-[200px] overflow-y-auto"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(waiver?.content || '<p>Loading waiver content...</p>') }}
          />

          {/* Signature Section */}
          <div className="mt-6 space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-lg font-semibold flex items-center gap-2">
                <PenTool className="h-5 w-5 text-primary" />
                Sign Below (Use finger or mouse)
              </Label>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearSignature}
                className="text-muted-foreground"
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                Clear
              </Button>
            </div>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg bg-white overflow-hidden">
              <SignatureCanvas
                ref={signatureRef}
                canvasProps={{
                  className: 'signature-canvas w-full h-[150px]',
                  style: { width: '100%', height: '150px' }
                }}
                backgroundColor="white"
                penColor="black"
              />
            </div>
            
            <p className="text-xs text-muted-foreground text-center">
              By signing above, you acknowledge that you have read and agree to the terms of this waiver.
            </p>
          </div>

          <div className="flex justify-end gap-4 mt-6">
            <Button variant="outline" onClick={() => setShowWaiver(false)}>
              Cancel
            </Button>
            <Button onClick={signWaiver} className="gap-2 bg-green-600 hover:bg-green-700">
              <CheckCircle2 className="h-4 w-4" />
              I Agree & Sign Waiver
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default BookingModal;
